import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
	ConnectionLineType,
	useNodesState,
	useEdgesState,
	Controls,
	Background,
	BackgroundVariant,
} from 'reactflow';
import {
	Button,
	ContentLayout,
	Header,
	SpaceBetween,
	Spinner,
	StatusIndicator,
	Box,
	Modal,
	Grid,
} from '@amzn/awsui-components-console';
import TaskNodeLegend from 'src/components/selfServeWorkflows/executionView/nodes/taskNode/legend/taskNodeLegend';
import TerminalNode from 'src/components/selfServeWorkflows/executionView/nodes/terminalNodes/terminalNode';
import TaskNode from 'src/components/selfServeWorkflows/executionView/nodes/taskNode/taskNode';
import TaskDetailsPanel from 'src/components/selfServeWorkflows/executionView/taskDetailsPanel';
import {
	generateGraphForDisplay,
	getLayoutedElements,
} from 'src/utils/selfServeUtils/reactFlowUtils/dagreLayout';
import { getTaskCountByStatus } from 'src/utils/selfServeUtils/commonUtils';
import { GraphLayoutDirection } from 'src/constants/selfServe/executionViewConstants';
import { ExecutionViewLayoutProps } from './workflowExecutionView.interface';
import {
	buildNestedExecutionGraph,
	isSubWorkflowTask,
	SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE,
} from './subWorkflowExecutionGraphLayout';
import SubWorkflowExecutionGroupNode from './nodes/subWorkflowGroupNode/subWorkflowExecutionGroupNode';
import { useSubWorkflowExecutions } from './useSubWorkflowExecutions';

export enum ReactFlowColors {
	button_gradient_color1 = 'var(--awsui-color-border-divider-default, #E5E4E2)',
	button_gradient_color2 = 'var(--awsui-color-border-divider-default, #ccc)',
}

const ExecutionViewLayout = ({
	taskExecutions,
	dependencies,
	showTaskDetails,
}: ExecutionViewLayoutProps) => {
	const [isLayouting, setIsLayouting] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [selectedTaskInModal, setSelectedTaskInModal] = useState<any>(null);
	const [inlineFlowInstance, setInlineFlowInstance] = useState<any>(null);
	const [modalFlowInstance, setModalFlowInstance] = useState<any>(null);
	const [showLargeGraphWarning, setShowLargeGraphWarning] = useState(false);
	const [graphLoaded, setGraphLoaded] = useState(false);
	const hasInitialized = React.useRef(false);
	const nodeCount = taskExecutions.length;
	const isLargeGraph = nodeCount > 150;

	const {
		expandedExecutionIds,
		childExecutions,
		loadingExecutionIds,
		errorsByExecutionId,
		toggleExpansion,
	} = useSubWorkflowExecutions();

	const hasExpanded = expandedExecutionIds.size > 0;

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [direction, setDirection] = useState<GraphLayoutDirection>(
		GraphLayoutDirection.TopToBottom
	);
	
	useEffect(() => {
		if (hasInitialized.current) return;
		
		if (isLargeGraph) {
			setShowLargeGraphWarning(true);
			setIsInitializing(false);
			hasInitialized.current = true;
			return;
		}
		
		setIsInitializing(true);
		hasInitialized.current = true;
		
		setTimeout(() => {
			const { nodes: builtNodes, edges: builtEdges } = buildGraph(
				GraphLayoutDirection.TopToBottom
			);
			setNodes(builtNodes);
			setEdges(builtEdges);
			setIsInitializing(false);
			setGraphLoaded(true);
		}, 50);
	}, [taskExecutions, dependencies, isLargeGraph, nodeCount]);

	useEffect(() => {
		if (!hasInitialized.current || !graphLoaded) return;

		const { nodes: builtNodes, edges: builtEdges } = buildGraph(direction);

		setNodes(builtNodes);
		setEdges(builtEdges);

		let innerFrame = 0;
		const outerFrame = requestAnimationFrame(() => {
			innerFrame = requestAnimationFrame(() => {
				const flowInstance = isFullscreen
					? modalFlowInstance
					: inlineFlowInstance;

				if (flowInstance) flowInstance.fitView();
			});
		});

		return () => {
			cancelAnimationFrame(outerFrame);
			cancelAnimationFrame(innerFrame);
		};
	}, [expandedExecutionIds, childExecutions]);

	useEffect(() => {
		if (!isFullscreen && inlineFlowInstance) {
			inlineFlowInstance.fitView();
		}
	}, [isFullscreen, inlineFlowInstance]);

	const taskStates = useMemo(() => getTaskCountByStatus(taskExecutions), [taskExecutions]);
	const nodeTypes = useMemo(
		() => ({
			taskNode: TaskNode,
			terminalNode: TerminalNode,
			[SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE]: SubWorkflowExecutionGroupNode,
		}),
		[]
	);

	const decorateSubWorkflowNodes = useCallback(
		(graphNodes) =>
			graphNodes.map((node) => {
				if (node.type === SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE) {
					return {
						...node,
						data: { ...node.data, onToggle: toggleExpansion },
					};
				}

				if (node.type !== 'taskNode') return node;

				if (!isSubWorkflowTask(node.data ?? {})) return node;

				return {
					...node,
					data: {
						...node.data,
						isSubWorkflow: true,
						isExpanded: expandedExecutionIds.has(
							node.data.childWorkflowExecutionId
						),
					},
				};
			}),
		[expandedExecutionIds, toggleExpansion]
	);

	const buildGraph = useCallback(
		(layoutDirection: GraphLayoutDirection) => {
			if (hasExpanded) {
				const nested = buildNestedExecutionGraph({
					taskExecutions,
					dependencies,
					expandedExecutionIds,
					childExecutions,
					direction: layoutDirection,
				});

				return {
					nodes: decorateSubWorkflowNodes(nested.nodes),
					edges: nested.edges,
				};
			}

			const base = generateGraphForDisplay(taskExecutions, dependencies);
			const relaid =
				layoutDirection === GraphLayoutDirection.TopToBottom
					? { nodes: base.layoutedNodes, edges: base.layoutedEdges }
					: getLayoutedElements(
						base.layoutedNodes,
						base.layoutedEdges,
						layoutDirection
					);

			return {
				nodes: decorateSubWorkflowNodes(relaid.nodes),
				edges: relaid.edges,
			};
		},
		[
			hasExpanded,
			taskExecutions,
			dependencies,
			expandedExecutionIds,
			childExecutions,
			decorateSubWorkflowNodes,
		]
	);

	const onLayout = useCallback(
		(nextDirection, isModal = false) => {
			setIsLayouting(true);
			setDirection(nextDirection);

			setTimeout(() => {
				const { nodes: layoutedNodes, edges: layoutedEdges } = hasExpanded
					? buildGraph(nextDirection)
					: getLayoutedElements(nodes, edges, nextDirection);

				setNodes([...layoutedNodes]);
				setEdges([...layoutedEdges]);

				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const flowInstance = isModal ? modalFlowInstance : inlineFlowInstance;
						if (flowInstance) {
							flowInstance.fitView();
						}
						setIsLayouting(false);
					});
				});
			}, 16);
		},
		[nodes, edges, inlineFlowInstance, modalFlowInstance, hasExpanded, buildGraph]
	);

	const handleLoadLargeGraph = useCallback(() => {
		setShowLargeGraphWarning(false);
		setIsInitializing(true);
		
		setTimeout(() => {
			const { nodes: builtNodes, edges: builtEdges } = buildGraph(direction);
			setNodes(builtNodes);
			setEdges(builtEdges);
			setIsInitializing(false);
			setGraphLoaded(true);
		}, 50);
	}, [buildGraph, direction]);

	const renderLargeGraphWarning = () => (
		<Box textAlign="center" padding="xxl">
			<Box variant="h3" margin={{ bottom: 'm' }}>
				Large Task Graph Detected
			</Box>
			<Box variant="p" color="text-body-secondary" margin={{ bottom: 'l' }}>
				This workflow contains {nodeCount} tasks. Loading the graphical view may take some time.
			</Box>
			<Button
				variant="primary"
				onClick={handleLoadLargeGraph}
				iconName="refresh"
			>
				Load Graph ({nodeCount} tasks)
			</Button>
		</Box>
	);

	const renderReactFlowCanvas = (isModal = false) => (
		<div 
			className={isModal ? "workflow-execution-canvas-modal" : "workflow-execution-canvas"}
			style={{ 
				cursor: (isLayouting || isInitializing) ? 'not-allowed' : 'default',
				pointerEvents: (isLayouting || isInitializing) ? 'none' : 'auto'
			}}
		>
			{showLargeGraphWarning ? (
				renderLargeGraphWarning()
			) : (isLayouting || isInitializing) ? (
				<Box textAlign="center" padding="xxl">
					<Spinner size="large" />
					<Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
						{isInitializing ? `Loading graph with ${nodeCount} Tasks...` : `Calculating layout for ${nodeCount} Tasks...`}
					</Box>
				</Box>
			) : (
				<ReactFlow
					key={isModal ? `modal-flow-${isFullscreen}` : `inline-flow-${isFullscreen ? 'hidden' : 'visible'}`}
					nodes={nodes}
					edges={edges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					connectionLineType={ConnectionLineType.Straight}
					fitView
					onInit={(instance) => {
						if (isModal) {
							setModalFlowInstance(instance);
						} else {
							setInlineFlowInstance(instance);
						}
					}}
					nodeTypes={nodeTypes}
					onNodeClick={(_, node) => {
						const { data, type } = node;

						if (type === SUB_WORKFLOW_EXECUTION_GROUP_NODE_TYPE) {
							toggleExpansion({
								childWorkflowId: data.childWorkflowId,
								childWorkflowExecutionId: data.childExecutionId,
								workflowName: data.workflowName,
							});

							return;
						}

						if (isSubWorkflowTask(data ?? {})) {
							toggleExpansion({
								childWorkflowId: data.childWorkflowId,
								childWorkflowExecutionId: data.childWorkflowExecutionId,
								workflowName: data.taskName,
							});

							return;
						}

						if (isModal) {
							setSelectedTaskInModal(data);
						} else {
							showTaskDetails(data);
						}
					}}
					onPaneClick={() => {
						if (isModal) {
							setSelectedTaskInModal(null);
						} else {
							showTaskDetails({});
						}
					}}
					panOnScroll
					elevateEdgesOnSelect={false}
					nodesDraggable={false}
					nodesConnectable={false}
					elementsSelectable={true}
					selectNodesOnDrag={false}
					minZoom={0.1}
					maxZoom={1.5}
				>
					<Controls />
					{isModal ? (
						<>
							<Background
								id="1"
								gap={1}
								color={ReactFlowColors.button_gradient_color1}
								variant={BackgroundVariant.Lines}
							/>
							<Background
								id="2"
								gap={10}
								color={ReactFlowColors.button_gradient_color1}
								variant={BackgroundVariant.Lines}
							/>
						</>
					) : (
						<Background
							id="1"
							gap={15}
							color={ReactFlowColors.button_gradient_color1}
							variant={BackgroundVariant.Dots}
						/>
					)}
				</ReactFlow>
			)}
		</div>
	);

	return (
		<>
			{/* Global cursor styling during loading */}
			{(isLayouting || isInitializing) && !showLargeGraphWarning && (
				<style>{`
					* { cursor: not-allowed !important; }
					body { pointer-events: none; }
				`}</style>
			)}
			<ContentLayout
				data-testid="workflow-execution-panel"
				header={
					<SpaceBetween size="m">
						<Header
							variant="h2"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										iconName="view-horizontal"
										variant="icon"
										onClick={() => onLayout(GraphLayoutDirection.TopToBottom, false)}
										disabled={isLayouting || isInitializing || showLargeGraphWarning}
										ariaLabel="Vertical Layout"
									>
										Vertical
									</Button>
									<Button
										iconName="view-vertical"
										variant="icon"
										onClick={() => onLayout(GraphLayoutDirection.LeftToRight, false)}
										disabled={isLayouting || isInitializing || showLargeGraphWarning}
										ariaLabel="Horizontal Layout"
									>
										Horizontal
									</Button>
									<Button
										iconName="expand"
										variant="icon"
										onClick={() => setIsFullscreen(true)}
										disabled={isLayouting || isInitializing || showLargeGraphWarning}
										ariaLabel="Expand to Fullscreen"
									>
										Expand
									</Button>
								</SpaceBetween>
							}
						>
							Graphical Execution View
						</Header>
						{loadingExecutionIds.size > 0 && (
							<Box color="text-body-secondary">
								<SpaceBetween direction="horizontal" size="xs">
									<Spinner size="normal" />
									<span>Loading sub-workflow tasks...</span>
								</SpaceBetween>
							</Box>
						)}
						{errorsByExecutionId.size > 0 && (
							<SpaceBetween size="xxs">
								{Array.from(errorsByExecutionId.values()).map(
									(message, index) => (
										<StatusIndicator key={index} type="error">
											Failed to load sub-workflow tasks: {message}
										</StatusIndicator>
									)
								)}
							</SpaceBetween>
						)}
					</SpaceBetween>
				}
			>
				<>
					{!isFullscreen && renderReactFlowCanvas(false)}
					{!showLargeGraphWarning && graphLoaded && <TaskNodeLegend {...taskStates} />}
				</>
			</ContentLayout>

			{isFullscreen && (
				<Modal
					visible={isFullscreen}
					onDismiss={() => {
						setIsFullscreen(false);
						setSelectedTaskInModal(null);
					}}
					size="max"
					header={
						<Header
							variant="h2"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										iconName="view-horizontal"
										variant="icon"
										onClick={() => onLayout(GraphLayoutDirection.TopToBottom, true)}
										disabled={isLayouting}
										ariaLabel="Vertical Layout"
									>
										Vertical
									</Button>
									<Button
										iconName="view-vertical"
										variant="icon"
										onClick={() => onLayout(GraphLayoutDirection.LeftToRight, true)}
										disabled={isLayouting}
										ariaLabel="Horizontal Layout"
									>
										Horizontal
									</Button>
								</SpaceBetween>
							}
						>
							Graphical Execution View
						</Header>
					}
				>
					<Grid
						gridDefinition={[
							{ colspan: selectedTaskInModal && Object.keys(selectedTaskInModal).length > 0 ? 9 : 12 },
							{ colspan: selectedTaskInModal && Object.keys(selectedTaskInModal).length > 0 ? 3 : 0 },
						]}
					>
						<div>
							{renderReactFlowCanvas(true)}
							{!showLargeGraphWarning && graphLoaded && <TaskNodeLegend {...taskStates} />}
						</div>
						{selectedTaskInModal && Object.keys(selectedTaskInModal).length > 0 && (
							<TaskDetailsPanel
								taskDetails={selectedTaskInModal}
								showTaskDetails={() => setSelectedTaskInModal(null)}
								refreshView={() => setSelectedTaskInModal(null)}
								hasExecutePermissions={false}
							/>
						)}
					</Grid>
				</Modal>
			)}
		</>
	);
};

export default ExecutionViewLayout;
